import { DownloadIcon } from "@cynthai/icons";
import { Button, Chip, InfoTipHint, Input } from "@cynthai/ui";
import { type ReactNode, useEffect, useState } from "react";

import { triggerBrowserDownload } from "@/core/lib/api";
import { apiErrorData, isApiError } from "@/core/lib/api-errors";
import { addToast } from "@/core/lib/toast";
import { StatementTreeCsvImportButton } from "@/features/classification/components/template-form/StatementTreeCsvImportButton";
import { StatementTreeLayout } from "@/features/classification/components/template-form/StatementTreeLayout";
import { StatementTreeRowsEditor } from "@/features/classification/components/template-form/StatementTreeRowsEditor";
import { useDraftRows } from "@/features/classification/hooks/use-draft-rows";
import {
  useStatementTaxonomy,
  useStatementTaxonomyMutations,
} from "@/features/classification/hooks/use-statement-taxonomies";
import {
  duplicateRowsDescription,
  publishIssueMessages,
  toDraftRows,
  toWriteRows,
} from "@/features/classification/lib/statement-tree";
import {
  buildStatementTreeCsv,
  parseStatementTreeCsv,
} from "@/features/classification/lib/statement-tree-csv";
import type {
  PublishStatementTaxonomyError,
  UpdateStatementTaxonomyError,
} from "@/openapi";
import { m } from "@/paraglide/messages";

export function StatementTaxonomyEditor({
  taxonomyId,
  sourceQuery,
  canImportCsv = false,
}: Readonly<{
  taxonomyId: string | null;
  sourceQuery?: ReactNode;
  canImportCsv?: boolean;
}>) {
  const taxonomy = useStatementTaxonomy(taxonomyId);
  const { organizationId, update, publish } = useStatementTaxonomyMutations();
  const [name, setName] = useState("");
  const draft = useDraftRows();
  const { rows, setRows } = draft;
  const isReadOnly = taxonomyId === null;

  useEffect(() => {
    if (!taxonomy.data) return;
    setName(taxonomy.data.name);
    setRows(toDraftRows(taxonomy.data.rows));
  }, [taxonomy.data, setRows]);

  const exportCsv = () => {
    const csv = buildStatementTreeCsv(rows);

    if (parseStatementTreeCsv(csv).status !== "ok") {
      addToast({
        title: m.statement_tree_csv_export_invalid_rows(),
        severity: "warning",
      });

      return;
    }

    triggerBrowserDownload(
      new Blob([csv], {
        type: "text/csv;charset=utf-8",
      }),
      `statement-tree-${taxonomyId ?? "default"}.csv`,
    );
  };

  const save = async () => {
    if (!taxonomyId) return;
    try {
      await update.mutateAsync({
        path: { org_id: organizationId, taxonomy_id: taxonomyId },
        body: {
          name: name.trim(),
          rows: toWriteRows(rows),
        },
      });
      await taxonomy.refetch();
      addToast({
        title: m.statement_taxonomy_editor_saved(),
        severity: "success",
      });
    } catch (error) {
      if (
        isApiError<UpdateStatementTaxonomyError>(error) &&
        isApiError(error, "STATEMENT_TAXONOMY_DUPLICATE_ROWS")
      ) {
        addToast({
          title: m.statement_tree_duplicate_rows(),
          description: m.statement_tree_duplicate_rows_description_$paths({
            paths: duplicateRowsDescription(
              rows,
              apiErrorData(error).duplicate_row_indexes,
            ),
          }),
          severity: "danger",
          error,
        });
        return;
      }
      addToast({ title: m.error_occurred(), severity: "danger", error });
    }
  };

  const publishDraft = async () => {
    if (!taxonomyId) return;
    try {
      await publish.mutateAsync({
        path: { org_id: organizationId, taxonomy_id: taxonomyId },
      });
      await taxonomy.refetch();
      addToast({
        title: m.statement_taxonomy_editor_published(),
        severity: "success",
      });
    } catch (error) {
      if (
        isApiError<PublishStatementTaxonomyError>(error) &&
        isApiError(error, "STATEMENT_TAXONOMY_EXTRACTION_IN_PROGRESS")
      ) {
        addToast({
          title: m.statement_taxonomy_extraction_in_progress(),
          severity: "danger",
          error,
        });

        return;
      }

      const issues =
        isApiError<PublishStatementTaxonomyError>(error) &&
        isApiError(error, "STATEMENT_TAXONOMY_NOT_PUBLISHABLE")
          ? publishIssueMessages(apiErrorData(error)?.issues ?? [])
          : [];
      addToast({
        title: issues[0] ?? m.error_occurred(),
        description: issues.slice(1).join(" · ") || undefined,
        severity: "danger",
        error,
      });
    }
  };

  return (
    <StatementTreeLayout
      identity={
        <>
          <Input
            size="custom"
            variant="bordered"
            className="max-w-xl flex-1"
            disabled={isReadOnly}
            label={m.statement_taxonomy_create_name()}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          {taxonomy.data && (
            <Chip
              size="sm"
              color={isReadOnly ? "default" : "tertiary"}
              className="mb-2 self-end"
            >
              {taxonomy.data.status === "published"
                ? m.statement_taxonomy_status_published()
                : isReadOnly
                  ? m.statement_taxonomy_status_default()
                  : m.statement_taxonomy_status_draft()}
            </Chip>
          )}
        </>
      }
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="bordered"
            color="secondary"
            icon={DownloadIcon}
            isDisabled={!taxonomy.data || rows.length === 0}
            onPress={exportCsv}
          >
            {m.statement_tree_csv_export()}
          </Button>

          {!isReadOnly && (
            <>
              {canImportCsv && (
                <StatementTreeCsvImportButton
                  isDisabled={!taxonomy.data || update.isPending}
                  onImport={setRows}
                />
              )}
              <Button
                size="sm"
                variant="ghost-light-bordered"
                isLoading={update.isPending}
                onPress={save}
              >
                {m.statement_taxonomy_editor_save()}
              </Button>
              <Button
                size="sm"
                color="primary"
                isLoading={publish.isPending}
                isDisabled={
                  taxonomy.data?.status !== "draft" || update.isPending
                }
                onPress={publishDraft}
              >
                {m.statement_taxonomy_editor_publish()}
              </Button>
            </>
          )}
        </div>
      }
      notice={
        (isReadOnly || canImportCsv) && (
          <div className="space-y-2">
            {isReadOnly && (
              <p className="text-text-tertiary text-sm">
                {m.statement_taxonomy_editor_readonly()}
              </p>
            )}
            {canImportCsv && (
              <InfoTipHint
                label={m.statement_tree_csv_import_format()}
                className="flex-col items-start gap-1.5"
              >
                <span className="block max-w-4xl leading-relaxed text-pretty">
                  {m.statement_tree_csv_import_hint()}
                </span>
              </InfoTipHint>
            )}
          </div>
        )
      }
      sourceQuery={sourceQuery}
      rows={
        <StatementTreeRowsEditor
          rows={rows}
          isReadOnly={isReadOnly}
          onAdd={draft.add}
          onUpdate={draft.update}
          onRemove={draft.remove}
          onReorder={draft.reorder}
        />
      }
    />
  );
}
